import { useEffect, useMemo, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { AlertCircle } from 'lucide-react'
import { format } from 'date-fns'
import { DayPicker } from 'react-day-picker'
import 'react-day-picker/dist/style.css'
import { Dialog, DialogContent, DialogTitle, DialogHeader } from '@/components/ui/dialog'
import {
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import * as SelectPrimitive from '@radix-ui/react-select'
import { ChevronDown, ChevronUp, Check } from 'lucide-react'
import type { AnswerValue, PhaseInfo, WorkflowStage } from '../../types/workflow'

const MONTH_OPTIONS = [
  { value: '0', label: 'January' },
  { value: '1', label: 'February' },
  { value: '2', label: 'March' },
  { value: '3', label: 'April' },
  { value: '4', label: 'May' },
  { value: '5', label: 'June' },
  { value: '6', label: 'July' },
  { value: '7', label: 'August' },
  { value: '8', label: 'September' },
  { value: '9', label: 'October' },
  { value: '10', label: 'November' },
  { value: '11', label: 'December' },
]

const currentYearNum = new Date().getFullYear()
const YEAR_OPTIONS: { value: string; label: string }[] = []
for (let y = currentYearNum; y >= 1900; y--) {
  YEAR_OPTIONS.push({ value: y.toString(), label: y.toString() })
}

interface BasicsTemplateProps {
  stage: WorkflowStage
  initialValue?: { date_of_birth?: string; sex_at_birth?: string }
  onSelect: (value: AnswerValue) => void
  currentPhase?: PhaseInfo | null
}

const inputCls =
  'w-full py-3.5 px-4 text-[15px] bg-[var(--ww-surface)] border border-[var(--ww-glass-border)] rounded-xl outline-none transition-all duration-200 text-[var(--ww-text)] placeholder:text-[var(--ww-text-3)] focus:border-[var(--ww-accent)] focus:shadow-[0_0_0_3px_var(--ww-accent-dim)] glass'

const btnPrimary =
  'w-full py-4 px-6 text-[15px] font-semibold text-[var(--ww-btn-text)] bg-[var(--ww-btn-bg)] border-none rounded-full cursor-pointer transition-all duration-300 shadow-[0_2px_8px_rgba(0,0,0,0.1)] hover:not-disabled:bg-[var(--ww-btn-hover)] hover:not-disabled:-translate-y-0.5 hover:not-disabled:shadow-[0_8px_24px_rgba(0,0,0,0.15)] active:not-disabled:translate-y-0 disabled:opacity-25 disabled:cursor-default'

function choiceBtn(active: boolean) {
  return `flex-1 py-4 text-[15px] font-medium rounded-2xl cursor-pointer transition-all duration-300 border glass flex items-center justify-center gap-2 ${active
    ? 'choice-selected border-[var(--ww-accent)] text-[var(--ww-accent)] shadow-[0_0_16px_rgba(74,138,154,0.12),inset_0_1px_0_rgba(74,138,154,0.15)] selected-glow'
    : 'bg-[var(--ww-surface)] border-[var(--ww-glass-border)] text-[var(--ww-text-2)] hover:border-[var(--ww-border-hover)] hover:text-[var(--ww-text)]'
    }`
}

function formatDateInput(value: string): string {
  const numbers = value.replace(/\D/g, '')
  if (numbers.length <= 2) return numbers
  if (numbers.length <= 4) return `${numbers.slice(0, 2)}/${numbers.slice(2)}`
  return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}/${numbers.slice(4, 8)}`
}

function validateDateStr(dateString: string | undefined): boolean {
  if (!dateString || dateString.length !== 10) return false
  const [month, day, year] = dateString.split('/').map(Number)
  if (month < 1 || month > 12) return false
  if (day < 1 || day > 31) return false
  if (year < 1900 || year > new Date().getFullYear()) return false
  const date = new Date(year, month - 1, day)
  return date.getMonth() === month - 1 && date.getDate() === day && date.getFullYear() === year
}

function calculateAge(dob: Date): number {
  const today = new Date()
  let age = today.getFullYear() - dob.getFullYear()
  const monthDiff = today.getMonth() - dob.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) age--
  return age
}

export default function BasicsTemplate({
  initialValue,
  onSelect,
}: BasicsTemplateProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dob, setDob] = useState('')
  const [sexAtBirth, setSexAtBirth] = useState('')
  const [ageError, setAgeError] = useState('')
  const [isCalendarOpen, setIsCalendarOpen] = useState(false)
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [currentMonth, setCurrentMonth] = useState(0)
  const [currentYear, setCurrentYear] = useState(1990)

  useEffect(() => {
    if (initialValue?.date_of_birth) {
      const parts = initialValue.date_of_birth.split('-')
      if (parts.length === 3) {
        setDob(`${parts[1]}/${parts[2]}/${parts[0]}`)
      }
    }
    if (initialValue?.sex_at_birth) setSexAtBirth(initialValue.sex_at_birth)
  }, [initialValue])

  useEffect(() => {
    const timer = setTimeout(() => inputRef.current?.focus(), 350)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (dob && validateDateStr(dob)) {
      const [month, day, year] = dob.split('/').map(Number)
      const parsedDate = new Date(year, month - 1, day)
      setSelectedDate(parsedDate)
      setCurrentMonth(month - 1)
      setCurrentYear(year)
    } else {
      setSelectedDate(null)
    }
  }, [dob])

  const computedAge = useMemo(() => {
    if (!dob || !validateDateStr(dob)) return null
    const [month, day, year] = dob.split('/').map(Number)
    return calculateAge(new Date(year, month - 1, day))
  }, [dob])

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDob(formatDateInput(e.target.value))
    if (ageError) setAgeError('')
  }

  const handleDateBlur = () => {
    if (dob && !validateDateStr(dob)) {
      setAgeError('Please enter a valid date in MM/DD/YYYY format')
    } else if (dob && validateDateStr(dob)) {
      if (computedAge !== null && computedAge < 18) {
        setAgeError('You must be at least 18 years old')
      } else {
        setAgeError('')
      }
    }
  }

  const handleCalendarOpen = () => {
    if (dob && validateDateStr(dob)) {
      const [month, , year] = dob.split('/').map(Number)
      setCurrentMonth(month - 1)
      setCurrentYear(year)
    }
    setIsCalendarOpen(true)
  }

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      setSelectedDate(date)
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const year = date.getFullYear()
      setDob(`${month}/${day}/${year}`)
      setAgeError('')
      setIsCalendarOpen(false)
    }
  }

  const maxDate = useMemo(() => {
    const d = new Date()
    d.setFullYear(d.getFullYear() - 18)
    d.setHours(23, 59, 59, 999)
    return d
  }, [])

  const isValid = useMemo(() => {
    if (!dob || !validateDateStr(dob) || !sexAtBirth) return false
    return computedAge !== null && computedAge >= 18 && computedAge <= 120
  }, [dob, sexAtBirth, computedAge])

  const handleContinue = () => {
    if (!isValid) return
    const [month, day, year] = dob.split('/').map(Number)
    const dateObj = new Date(year, month - 1, day)
    onSelect({
      date_of_birth: format(dateObj, 'yyyy-MM-dd'),
      sex_at_birth: sexAtBirth,
      age: computedAge,
    })
  }

  return (
    <div className="flex-1 flex flex-col gap-4">
      <fieldset className="border-none flex flex-col gap-2">
        <legend className="text-[13px] font-medium text-[var(--ww-text-2)] mb-1.5">Date of Birth</legend>
        <div className="relative">
          <input
            ref={inputRef}
            className={`${inputCls} pr-12`}
            type="text"
            inputMode="numeric"
            placeholder="MM/DD/YYYY"
            value={dob}
            onChange={handleDateChange}
            onBlur={handleDateBlur}
            onKeyDown={(e) => { if (e.key === 'Enter' && isValid) handleContinue() }}
            maxLength={10}
            autoFocus
            aria-label="Date of birth"
          />
          <button
            type="button"
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-[var(--ww-text-2)] hover:text-[var(--ww-accent)] hover:bg-[var(--ww-surface-2)] transition-colors duration-200 cursor-pointer"
            onClick={handleCalendarOpen}
            aria-label="Open calendar"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
            </svg>
          </button>
        </div>
        {ageError && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-start gap-2 text-[13px] text-[var(--ww-error,#f87171)] font-medium">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{ageError}</span>
          </motion.div>
        )}
        {!ageError && computedAge !== null && computedAge >= 18 && computedAge <= 120 && (
          <p className="text-[13px] text-[var(--ww-accent)] font-semibold m-0">
            Age: {computedAge} years old
          </p>
        )}
      </fieldset>

      <Dialog open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
        <DialogContent
          hideCloseButton
          className="!bg-[var(--ww-bg)] max-w-[380px] w-[92vw] border border-[var(--ww-glass-border)] p-5 rounded-2xl gap-0 shadow-[0_16px_48px_rgba(0,0,0,0.3)] overflow-visible"
        >
          <DialogHeader className="flex flex-row items-center justify-between mb-4 space-y-0">
            <DialogTitle className="text-[13px] font-semibold text-[var(--ww-accent)] uppercase tracking-wide">Select Date</DialogTitle>
            <button
              onClick={() => setIsCalendarOpen(false)}
              className="size-9 flex items-center justify-center rounded-lg text-[var(--ww-text-2)] hover:text-[var(--ww-text)] hover:bg-[var(--ww-surface-2)] transition-colors focus:outline-none cursor-pointer"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </DialogHeader>

          <div className="relative" style={{ isolation: 'isolate' }}>
            <style>{`
              /* Ensure Radix Select portal renders above everything */
              [data-radix-popper-content-wrapper] {
                z-index: 999999 !important;
              }
              /* Keep DayPicker grid below nav row stacking context */
              .custom-calendar,
              .custom-calendar .rdp-month_grid,
              .custom-calendar .rdp-week,
              .custom-calendar .rdp-day,
              .custom-calendar .rdp-day_button {
                position: relative;
                z-index: 0 !important;
              }
              /* DayPicker v9 — hide built-in nav & caption entirely */
              .custom-calendar .rdp-month_caption,
              .custom-calendar .rdp-nav {
                display: none !important;
              }
              /* DayPicker v9 CSS variables */
              .custom-calendar.rdp-root {
                --rdp-accent-color: var(--ww-accent);
                --rdp-accent-background-color: rgba(74,138,154,0.12);
                --rdp-selected-border: none;
                --rdp-today-color: var(--ww-accent);
                --rdp-day-height: 2.25rem;
                --rdp-day-width: 2.25rem;
                --rdp-day_button-height: 2.25rem;
                --rdp-day_button-width: 2.25rem;
                --rdp-day_button-border: none;
                --rdp-weekday-padding: 0.4rem 0;
                background: transparent !important;
                font-family: 'Montserrat', sans-serif !important;
              }
              /* Table fills full width with equal columns */
              .custom-calendar .rdp-month_grid {
                width: 100% !important;
                table-layout: fixed !important;
              }
              /* Weekday labels */
              .custom-calendar .rdp-weekday {
                color: var(--ww-text-2) !important;
                opacity: 1 !important;
                background: transparent !important;
                text-align: center !important;
                font-family: 'Montserrat', sans-serif !important;
                font-weight: 500 !important;
                font-size: 12px !important;
                letter-spacing: 0.02em !important;
              }
              .custom-calendar .rdp-weekdays,
              .custom-calendar thead,
              .custom-calendar thead tr {
                background: transparent !important;
              }
              /* Day cells */
              .custom-calendar .rdp-day {
                text-align: center !important;
              }
              /* Day buttons */
              .custom-calendar .rdp-day_button {
                color: var(--ww-text) !important;
                border: none !important;
                background: transparent !important;
                border-radius: 50% !important;
                font-family: 'Montserrat', sans-serif !important;
                font-size: 13px !important;
                font-weight: 400 !important;
                margin: 0 auto !important;
                transition: background 0.15s, color 0.15s;
              }
              .custom-calendar .rdp-day_button:hover:not(:disabled) {
                background: rgba(74,138,154,0.12) !important;
                color: var(--ww-accent) !important;
              }
              .custom-calendar .rdp-day_button:focus-visible {
                outline: none !important;
                box-shadow: none !important;
              }
              /* Selected — teal fill, white text */
              .custom-calendar .rdp-selected .rdp-day_button {
                background: var(--ww-accent) !important;
                color: #ffffff !important;
                border: none !important;
                font-weight: 600 !important;
              }
              .custom-calendar .rdp-selected .rdp-day_button:hover {
                background: var(--ww-accent) !important;
                color: #ffffff !important;
              }
              /* Today */
              .custom-calendar .rdp-today:not(.rdp-outside) .rdp-day_button {
                background: rgba(74,138,154,0.12) !important;
                color: var(--ww-accent) !important;
              }
              /* Outside month — faded */
              .custom-calendar .rdp-outside .rdp-day_button {
                color: var(--ww-text-3) !important;
                opacity: 0.35 !important;
              }
              /* Disabled */
              .custom-calendar .rdp-disabled .rdp-day_button,
              .custom-calendar .rdp-day_button:disabled {
                color: var(--ww-text-3) !important;
                opacity: 0.3 !important;
                cursor: default !important;
                background: transparent !important;
              }
              /* Select trigger text — Montserrat */
              .ww-cal-select button,
              .ww-cal-select [role="combobox"],
              .ww-cal-select span {
                color: var(--ww-text) !important;
                font-family: 'Montserrat', sans-serif !important;
                font-size: 13px !important;
                font-weight: 500 !important;
              }
            `}</style>

            {/* Custom nav row — relative+z-index creates stacking context above DayPicker grid */}
            <div className="flex items-center justify-between mb-4 relative z-10">
              <button
                type="button"
                onClick={() => {
                  const d = new Date(currentYear, currentMonth - 1, 1)
                  setCurrentMonth(d.getMonth())
                  setCurrentYear(d.getFullYear())
                }}
                className="h-10 w-10 flex items-center justify-center rounded-lg text-[var(--ww-text-2)] hover:text-[var(--ww-text)] hover:bg-[var(--ww-surface-2)] transition-colors cursor-pointer focus:outline-none"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
              </button>

              <div className="flex items-center gap-1.5 ww-cal-select">
                {/* Month — no portal so it stays inside Dialog stacking context */}
                <SelectPrimitive.Root
                  value={currentMonth.toString()}
                  onValueChange={(v) => setCurrentMonth(parseInt(v))}
                >
                  <SelectTrigger
                    className="w-[120px] h-[40px] bg-[var(--ww-surface)] border border-[var(--ww-glass-border)] hover:border-[var(--ww-border-hover)] focus:border-[var(--ww-glass-border)] focus:ring-0 focus:ring-offset-0 rounded-xl text-[13px] font-medium transition-colors"
                    style={{ color: 'var(--ww-text)', fontFamily: "'Montserrat', sans-serif" }}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectPrimitive.Content
                    position="popper"
                    className="border border-[var(--ww-glass-border)] shadow-xl rounded-xl overflow-hidden p-1 relative"
                    style={{ zIndex: 999999, backgroundColor: '#0d1117' }}
                    avoidCollisions={false}
                  >
                    <SelectPrimitive.ScrollUpButton className="flex items-center justify-center py-1 text-[var(--ww-text-2)]">
                      <ChevronUp className="h-4 w-4" />
                    </SelectPrimitive.ScrollUpButton>
                    <SelectPrimitive.Viewport className="p-0">
                      {MONTH_OPTIONS.map((m) => (
                        <SelectPrimitive.Item
                          key={m.value}
                          value={m.value}
                          className="relative w-full rounded-lg text-[13px] font-medium cursor-pointer py-2.5 pl-8 pr-2 outline-none focus:bg-[var(--ww-accent)] focus:text-[#0a0a0a] data-[state=checked]:bg-[var(--ww-accent)] data-[state=checked]:text-[#0a0a0a] text-[var(--ww-text)] hover:bg-[var(--ww-surface-2)]"
                          style={{ fontFamily: "'Montserrat', sans-serif" }}
                        >
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 flex h-3.5 w-3.5 items-center justify-center">
                            <SelectPrimitive.ItemIndicator>
                              <Check className="h-3.5 w-3.5" />
                            </SelectPrimitive.ItemIndicator>
                          </span>
                          <SelectPrimitive.ItemText>{m.label}</SelectPrimitive.ItemText>
                        </SelectPrimitive.Item>
                      ))}
                    </SelectPrimitive.Viewport>
                    <SelectPrimitive.ScrollDownButton className="flex items-center justify-center py-1 text-[var(--ww-text-2)]">
                      <ChevronDown className="h-4 w-4" />
                    </SelectPrimitive.ScrollDownButton>
                  </SelectPrimitive.Content>
                </SelectPrimitive.Root>

                {/* Year — no portal so it stays inside Dialog stacking context */}
                <SelectPrimitive.Root
                  value={currentYear.toString()}
                  onValueChange={(v) => setCurrentYear(parseInt(v))}
                >
                  <SelectTrigger
                    className="w-[85px] h-[40px] bg-[var(--ww-surface)] border border-[var(--ww-glass-border)] hover:border-[var(--ww-border-hover)] focus:border-[var(--ww-glass-border)] focus:ring-0 focus:ring-offset-0 rounded-xl text-[13px] font-medium transition-colors"
                    style={{ color: 'var(--ww-text)', fontFamily: "'Montserrat', sans-serif" }}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectPrimitive.Content
                    position="popper"
                    className="border border-[var(--ww-glass-border)] max-h-[200px] shadow-xl rounded-xl overflow-hidden p-1 relative"
                    style={{ zIndex: 999999, backgroundColor: '#0d1117' }}
                    avoidCollisions={false}
                  >
                    <SelectPrimitive.ScrollUpButton className="flex items-center justify-center py-1 text-[var(--ww-text-2)]">
                      <ChevronUp className="h-4 w-4" />
                    </SelectPrimitive.ScrollUpButton>
                    <SelectPrimitive.Viewport className="p-0 max-h-[180px] overflow-y-auto">
                      {YEAR_OPTIONS.map((y) => (
                        <SelectPrimitive.Item
                          key={y.value}
                          value={y.value}
                          className="relative w-full rounded-lg text-[13px] font-medium cursor-pointer py-2.5 pl-8 pr-2 outline-none focus:bg-[var(--ww-accent)] focus:text-[#0a0a0a] data-[state=checked]:bg-[var(--ww-accent)] data-[state=checked]:text-[#0a0a0a] text-[var(--ww-text)] hover:bg-[var(--ww-surface-2)]"
                          style={{ fontFamily: "'Montserrat', sans-serif" }}
                        >
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 flex h-3.5 w-3.5 items-center justify-center">
                            <SelectPrimitive.ItemIndicator>
                              <Check className="h-3.5 w-3.5" />
                            </SelectPrimitive.ItemIndicator>
                          </span>
                          <SelectPrimitive.ItemText>{y.label}</SelectPrimitive.ItemText>
                        </SelectPrimitive.Item>
                      ))}
                    </SelectPrimitive.Viewport>
                    <SelectPrimitive.ScrollDownButton className="flex items-center justify-center py-1 text-[var(--ww-text-2)]">
                      <ChevronDown className="h-4 w-4" />
                    </SelectPrimitive.ScrollDownButton>
                  </SelectPrimitive.Content>
                </SelectPrimitive.Root>
              </div>

              <button
                type="button"
                onClick={() => {
                  const d = new Date(currentYear, currentMonth + 1, 1)
                  setCurrentMonth(d.getMonth())
                  setCurrentYear(d.getFullYear())
                }}
                className="h-10 w-10 flex items-center justify-center rounded-lg text-[var(--ww-text-2)] hover:text-[var(--ww-text)] hover:bg-[var(--ww-surface-2)] transition-colors cursor-pointer focus:outline-none"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
              </button>
            </div>

            {/* DayPicker grid — z-index: 0 keeps it below the nav row stacking context */}
            <div className="relative" style={{ zIndex: 0 }}>
              <DayPicker
                mode="single"
                selected={selectedDate || undefined}
                onSelect={handleDateSelect}
                month={new Date(currentYear, currentMonth)}
                onMonthChange={(date: Date) => {
                  setCurrentMonth(date.getMonth())
                  setCurrentYear(date.getFullYear())
                }}
                showOutsideDays
                className="custom-calendar w-full !p-0"
                disabled={(date: Date) => {
                  const today = new Date()
                  today.setHours(0, 0, 0, 0)
                  return date > maxDate || date > today
                }}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <fieldset className="border-none flex flex-col gap-2">
        <legend className="text-[13px] font-medium text-[var(--ww-text-2)] mb-1.5">Sex assigned at birth</legend>
        <div className="flex gap-2">
          <button className={choiceBtn(sexAtBirth === 'female')} onClick={() => setSexAtBirth('female')}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="9" r="6" /><path d="M12 15v7" /><path d="M9 19h6" />
            </svg>
            Female
          </button>
          <button className={choiceBtn(sexAtBirth === 'male')} onClick={() => setSexAtBirth('male')}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="10" cy="14" r="6" /><path d="M21 3h-6" /><path d="M21 3v6" /><path d="m21 3-7.5 7.5" />
            </svg>
            Male
          </button>
        </div>
      </fieldset>

      <div className="mt-auto pt-6 pb-2 -ml-[42px] w-[calc(100%+42px)]">
        <button className={btnPrimary} disabled={!isValid} onClick={handleContinue}>
          Continue
        </button>
      </div>
    </div>
  )
}
